import {
	formatTime as localFormatTime,
	formatMetDate,
} from 'helpers/date-time';
import { formatTime, getLocalMidnight } from 'lib/visit/locationSchedule';
import Image from 'next/image';
import RedundantLink from 'components/accesibility/redundant-link';
import styles from './event-card.module.scss';
import GTMLink from 'components/controls/links/GTMLink';
import LegacyOrMarkdownParser from 'components/rich-text/legacy-or-markdown-parser';
import { type ReactNode } from 'react';
import { useLocale } from 'next-intl';
import { ensureEngageLocale } from 'lib/engage';
import cn from 'classnames';
import hoverStyles from '../hover-state.module.scss';
import { getActionAttributes } from 'lib/analytics/analyticsAttributes';

export type Props = {
	aceId?: string | number;
	buildingName: string;
	isPaid: boolean;
	startDate: string;
	teaserImageAltText: string;
	teaserImageUrl: string;
	teaserText: string;
	ticketPricing: string;
	title: string;
	url: string;
	forceCompact?: boolean;
	isEvent?: boolean;
	showDate?: boolean;
	body?: ReactNode;
	analytics?: {
		Content_Block?: string;
		event?: string;
	};
};

const EventCard = ({
	aceId,
	buildingName,
	isPaid,
	startDate,
	teaserImageAltText,
	teaserImageUrl,
	teaserText,
	ticketPricing,
	title,
	url,
	forceCompact = false,
	isEvent = false,
	showDate = false,
	body,
	analytics,
}: Props) => {
	const locale = useLocale();
	const analyticsAttributes = getActionAttributes({
		contentId: aceId,
		contentName: title,
	});
	const time = formatTime({
		// This is for display purposes on the client, so base the calculated
		// time on midnight on the client.
		date: getLocalMidnight(),
		// The original datetime is in NYC time, so extract the time component
		// so it can be added to midnight above to become the display date.
		// This avoids time zone calculation nuttiness.
		time: localFormatTime(startDate),
		locale,
		short: true,
	});
	const date = formatMetDate(startDate, locale);
	const cost =
		!isPaid && !ticketPricing ? 'Free with Museum admission' : ticketPricing;

	return (
		<div
			className={cn(
				styles.eventCard,
				forceCompact && styles.compact,
				isEvent && styles.eventModeCard,
				hoverStyles.card,
			)}
		>
			<figure
				className={`${styles.imageWrapper} ${isEvent ? styles.eventModeImageWrapper : ''}`}
			>
				{url && (
					<RedundantLink
						href={ensureEngageLocale(url, locale)}
						Content_Type={'Events and Tours'}
						Content_Name={title}
						Content_Block={analytics?.Content_Block}
						event={analytics?.event}
						{...analyticsAttributes}
					/>
				)}
				<Image
					sizes="(max-width: 960px) 90vw, 300px"
					className={styles.cardImage}
					src={teaserImageUrl}
					alt={teaserImageAltText}
					fill={true}
				/>
			</figure>
			<div className={styles.infoContainer}>
				<h4 className={styles.title}>
					<GTMLink
						href={ensureEngageLocale(url, locale)}
						Content_Type={'Events and Tours'}
						Content_Name={title}
						Content_Block={analytics?.Content_Block}
						event={analytics?.event}
						{...analyticsAttributes}
					>
						<LegacyOrMarkdownParser>{title}</LegacyOrMarkdownParser>
					</GTMLink>
				</h4>
				{!forceCompact && !!teaserText && (
					<div className={styles.description}>
						<LegacyOrMarkdownParser>{teaserText}</LegacyOrMarkdownParser>
					</div>
				)}

				{body ? (
					body
				) : (
					<>
						<div className={styles.timeAndPlace}>
							{showDate && <span>{date}</span>} <span>{time}</span>
							<span>{buildingName}</span>
						</div>
					</>
				)}
				{!!cost && isEvent && (
					<div className={styles.timeAndPlace}>
						<span>{cost}</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default EventCard;
